import { Oferta, OfertaLayout } from "../types/landingSections";
import { useProfesional } from "../context/ProfesionalContext";
import { ButtonPopup, ButtonVolver, Select } from "@/components/ui";
import { useNavigate, useParams } from "react-router-dom";
import { useState, useMemo, useEffect } from "react";
import { getOfertasLanding, updateOfertasLanding } from "../api/landin";
import {
  deleteOfertaLanding,
  subirImagenOfertaLanding,
} from "../subirImagen/subirImagen";
import Alerta from "@/components/alert/Alerta";
import { eliminarImagenEnServidor } from "@/components/utils";
import OfertaCard from "./cards/CardsNormal";
import CardsGrande from "./cards/CardsGrande";

const EditarOfertasLanding = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [alerta, setAlerta] = useState<{
    mensaje: string;
    type?: "success" | "error" | "warning" | "info";
  } | null>(null);

  const { perfilId, setLandingData, landingData } = useProfesional();
  const ofertasSection = landingData?.home?.ofertas?.sections?.[0];
  const ofertas = ofertasSection?.ofertas ?? [];
  const layout = ofertasSection?.layout ?? "grid";
  const [currentView, setCurrentView] = useState<OfertaLayout>(layout);
  const MAX_OFERTAS = 30;

  const { profesionalId, profesionId } = useParams();
  
  // ===========================
  // Cargar las ofertas que hay
  // ===========================
  useEffect(() => {
    if (!perfilId) return;

    const cargarOfertas = async () => {
      const res = await getOfertasLanding(perfilId);

      setLandingData((prev) => {
        if (!prev) return prev;

        const section = prev.home.ofertas.sections?.[0];
        if (!section) return prev;

        return {
          ...prev,
          home: {
            ...prev.home,
            ofertas: {
              ...prev.home.ofertas,
              sections: prev.home.ofertas.sections.map((s) =>
                s.id === section.id ? { ...s, ofertas: res.data } : s,
              ),
            },
          },
        };
      });
    };

    cargarOfertas();
  }, [perfilId]);

  // =========================
  // SAFE ACCESS
  // =========================

  // =========================
  // DERIVED LISTS
  // =========================
  const carouselOfertas = useMemo(
    () => ofertas.filter((o) => (o.layout ?? layout) === "carousel"),
    [ofertas, layout],
  );

  const gridOfertas = useMemo(
    () => ofertas.filter((o) => (o.layout ?? layout) === "grid"),
    [ofertas, layout],
  );

  const wideOfertas = useMemo(
    () => ofertas.filter((o) => (o.layout ?? layout) === "wide"),
    [ofertas, layout],
  );

  // =========================
  // GUARD
  // =========================
  if (!perfilId || !landingData) return <div>Cargando...</div>;
  if (!ofertasSection) return null;

  // =========================
  // UPDATE STATE
  // =========================
  const updateOfertas = (next: Oferta[]) => {
    setLandingData({
      ...landingData!,
      home: {
        ...landingData!.home,
        ofertas: {
          ...landingData!.home.ofertas,
          sections: landingData!.home.ofertas.sections.map((s) =>
            s.id === ofertasSection.id ? { ...s, ofertas: next } : s,
          ),
        },
      },
    });
  };

  // =========================
  // UPDATE FIELD
  // =========================
  const updateOferta = <K extends keyof Oferta>(
    id: string,
    field: K,
    value: Oferta[K],
  ) => {
    updateOfertas(
      ofertas.map((o) => (o.id === id ? { ...o, [field]: value } : o)),
    );
  };

  // =========================
  // ADD OFERTA (SOLO UI)
  // =========================

  const addOferta = () => {
    if (ofertas.length >= MAX_OFERTAS) {
      setAlerta({
        mensaje: `No puedes tener más de ${MAX_OFERTAS} cards en total entre Carrusel, Cards normales y Cards grandes.`,
        type: "warning",
      });
      return;
    }

    const newOferta: Oferta = {
      id: crypto.randomUUID(),
      titulo: "",
      descripcion: "",
      precio: 0,
      imagenes: [],
      layout: currentView,
    };

    updateOfertas([...ofertas, newOferta]);
  };

  // =========================
  // SAVE
  // =========================
  const handleSave = async () => {
    try {
      setLoading(true);

      // 1. Guardar ofertas (sin imágenes reales todavía si ya estás usando preview)
      await updateOfertasLanding(perfilId, ofertas);

      // 2. Subir imágenes pendientes SOLO después de guardar
      const ofertasConImagen = ofertas.filter(
        (o): o is Oferta & { _pendingFile: File } =>
          Boolean((o as any)._pendingFile),
      );

      for (const oferta of ofertasConImagen) {
        // Imagen antigua
        const imagenAnterior = ofertas.find((o) => o.id === oferta.id)
          ?.imagenes?.[0];

        // Subir nueva
        const uploaded = await subirImagenOfertaLanding(
          oferta._pendingFile,
          perfilId,
          oferta.id!,
        );

        // Si existía una imagen distinta, borrarla de S3
        if (imagenAnterior && imagenAnterior !== uploaded.url) {
          try {
            await eliminarImagenEnServidor(imagenAnterior, "ofertas");
          } catch (err) {
            console.error("Error eliminando imagen antigua:", err);
          }
        }

        // Actualizar estado
        updateOfertas(
          ofertas.map((o) =>
            o.id === oferta.id
              ? {
                  ...o,
                  imagenes: [uploaded.url],
                  _pendingFile: undefined,
                }
              : o,
          ),
        );
      }

      setAlerta({
        mensaje: "Ofertas guardadas correctamente",
        type: "success",
      });
    } catch (e) {
      console.error(e);

      setAlerta({
        mensaje: "Error guardando ofertas",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // DELETE
  // =========================
  const handleRemoveOferta = async (ofertaId: string) => {
    try {
      const response = await deleteOfertaLanding(ofertaId);

      if (response.images?.length) {
        await Promise.allSettled(
          response.images.map((img) =>
            eliminarImagenEnServidor(img, "ofertas"),
          ),
        );
      }

      updateOfertas(ofertas.filter((o) => o.id !== ofertaId));

      setAlerta({
        mensaje: "Oferta eliminada correctamente",
        type: "success",
      });
    } catch (error) {
      console.error(error);

      setAlerta({
        mensaje: "Error eliminando oferta",
        type: "error",
      });
    }
  };

  const updateLayout = (newLayout: OfertaLayout) => {
    setCurrentView(newLayout);

    setLandingData({
      ...landingData!,
      home: {
        ...landingData!.home,
        ofertas: {
          ...landingData!.home.ofertas,
          sections: landingData!.home.ofertas.sections.map((s) =>
            s.id === ofertasSection.id
              ? {
                  ...s,
                  layout: newLayout,
                }
              : s,
          ),
        },
      },
    });
  };

  const card = `relative overflow-hidden rounded-lg border-r-4 border-t-2 border-cyan-300/50 bg-gradient-to-br from-white/10 via-white/5 to-transparent backdrop-blur-3xl shadow-md shadow-zinc-800 p-8`;

  // =========================
  // UI RETURN
  // =========================
  return (
    <>
      {alerta && (
        <Alerta
          mensaje={alerta.mensaje}
          type={alerta.type}
          onClose={() => setAlerta(null)}
        />
      )}

      <div className=" text-zinc-900 py-16 px-4 overflow-x-hidden">
        <div className="max-w-7xl mx-auto space-y-10">
          {/* Volver */}
          <div className="absolute top-4 left-4">
            <ButtonVolver
              onClick={() =>
                navigate(
                  `/editar-landing/${profesionalId}/${profesionId}/${perfilId}`,
                )
              }
            />
          </div>

          {/* HEADER */}
          <div
            className="
    w-full
    rounded-lg
    border-t-2 border-b-2 border-cyan-300
    bg-zinc-950
    py-2 px-8
    shadow-md
    shadow-zinc-800
    text-white
    backdrop-blur-xl
    mb-10
    mt-5
  "
          >
            <div
              className="
      flex flex-col
      gap-8
      md:flex-row
      md:items-center
      md:justify-between
    "
            >
              {/* INFO */}
              <div className="space-y-3 flex-1">
                <h1 className="text-3xl font-bold tracking-tight">
                  Editor de Ofertas
                </h1>

                <p className="text-zinc-400 text-sm max-w-md">
                  Gestiona las ofertas que aparecerán en tu landing.
                </p>

                <p className="text-cyan-300 text-sm">
                  {ofertas.length}/{MAX_OFERTAS} ofertas creadas
                </p>
              </div>

              {/* BOTONES */}
              <div
                className="
        flex
        flex-col
        sm:flex-row
        sm:justify-end
        sm:items-center
        gap-3
        w-full
        md:w-auto
      "
              >
                <ButtonPopup
                  onClick={addOferta}
                  disabled={ofertas.length >= MAX_OFERTAS}
                  className="
          px-4 py-2
          rounded-lg
          bg-zinc-900
          border border-white/10
          hover:border-green-500
          transition
          flex items-center justify-center
          whitespace-nowrap
          overflow-hidden
          text-ellipsis
          max-w-full
        "
                >
                  {ofertas.length >= MAX_OFERTAS
                    ? "Máximo alcanzado"
                    : "Añadir oferta"}
                </ButtonPopup>

                <ButtonPopup
                  onClick={handleSave}
                  disabled={loading}
                  className="
          px-4 py-2
          rounded-lg
          bg-zinc-900
          border border-white/10
          hover:border-cyan-500
          transition
          flex items-center justify-center
          whitespace-nowrap
          overflow-hidden
          text-ellipsis
          max-w-full
        "
                >
                  {loading ? "Guardando..." : "Guardar cambios"}
                </ButtonPopup>
              </div>
            </div>
          </div>

          {/* CONFIGURACIÓN */}
          <section className={card}>
            <div className="flex flex-col lg:flex-row lg:items-end gap-6">
              <div className="flex-1">
                <h2 className="text-xl font-semibold mb-2">
                  Configuración de visualización
                </h2>

                <p className="text-zinc-400 text-sm mb-5">
                  Elige cómo quieres mostrar las ofertas en la landing.
                </p>

                <div className="max-w-sm">
                  <Select
                    value={currentView}
                    onChange={(e) =>
                      updateLayout(e.target.value as OfertaLayout)
                    }
                    options={[
                      {
                        value: "carousel",
                        label: "Carrusel",
                      },
                      {
                        value: "grid",
                        label: "Cards normales",
                      },
                      {
                        value: "wide",
                        label: "Cards grandes",
                      },
                    ]}
                  />
                </div>
              </div>
            </div>
          </section>

          {/* OFERTAS */}

          {currentView === "carousel" && (
            <section className={card}>
              {carouselOfertas.length === 0 ? (
                <div className="text-center text-zinc-500 py-16">
                  No hay ofertas de tipo <b>Carrusel</b>.
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-6">
                  {carouselOfertas.map((o) => (
                    <OfertaCard
                      key={o.id}
                      o={o}
                      ofertas={ofertas}
                      updateOferta={updateOferta}
                      updateOfertas={updateOfertas}
                      handleRemoveOferta={handleRemoveOferta}
                    />
                  ))}
                </div>
              )}
            </section>
          )}

          {currentView === "grid" && (
            <section className={card}>
              {gridOfertas.length === 0 ? (
                <div className="text-center text-zinc-500 py-16">
                  No hay ofertas de tipo <b>Cards normales</b>.
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-6">
                  {gridOfertas.map((o) => (
                    <OfertaCard
                      key={o.id}
                      o={o}
                      ofertas={ofertas}
                      updateOferta={updateOferta}
                      updateOfertas={updateOfertas}
                      handleRemoveOferta={handleRemoveOferta}
                    />
                  ))}
                </div>
              )}
            </section>
          )}

          {currentView === "wide" && (
            <section className={card}>
              {wideOfertas.length === 0 ? (
                <div className="text-center text-zinc-500 py-16">
                  No hay ofertas de tipo <b>Cards grandes</b>.
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                  {wideOfertas.map((o) => (
                    <CardsGrande
                      key={o.id}
                      oferta={o}
                      ofertas={ofertas}
                      updateOferta={updateOferta}
                      updateOfertas={updateOfertas}
                      handleRemoveOferta={handleRemoveOferta}
                    />
                  ))}
                </div>
              )}
            </section>
          )}
        </div>
      </div>
    </>
  );
};

export default EditarOfertasLanding;